import {
  getFontCss,
  Popup,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  autoMatchReceipts,
  createSetReceiptAutoMatchAttachmentIds,
} from 'src/app/UploadReceiptsAutoMatchPopup/redux/uploadReceiptsAutoMatch.actions'
import {
  selectReceiptAttachmentModelsList,
  selectReceiptsAutoMatchErrorFromState,
  selectUploadingAttachmentsFromState,
} from 'src/app/UploadReceiptsAutoMatchPopup/redux/uploadReceiptsAutoMatch.selectors'
import AttachmentsList from 'src/components/AttachmentsList/AttachmentsList'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import ErrorList from 'src/components/ErrorList/ErrorList'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import SvgReceiptUpload from 'src/components/Svgs/SvgReceiptUpload'
import { useTranslate } from 'src/localization/Trns'
import { useUploadReceiptAttachments } from 'src/queries/receiptAttachments/receiptAttachments.queries'
import { createSetIsUploadReceiptsAutoMatchPopupOpen } from 'src/redux/actions/app.actions'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { css } from 'styled-components'

const UploadReceiptsAutoMatchPopup: React.FC<
  React.PropsWithChildren
> = (): React.ReactElement => {
  const translate = useTranslate()

  const receiptAttachments = useSelector(selectReceiptAttachmentModelsList)
  const uploadingAttachmentThumbnails = useSelector(
    selectUploadingAttachmentsFromState,
  )
  const receiptsAutoMatchError = useSelector(
    selectReceiptsAutoMatchErrorFromState,
  )

  const [attachmentError, setAttachmentError] = useState<
    string | React.ReactElement | undefined
  >()

  const dispatch = useDispatch()
  const uploadReceiptAttachments = useUploadReceiptAttachments({
    dispatchMode: 'redux',
  })

  const closePopup = (): void => {
    dispatch(createSetIsUploadReceiptsAutoMatchPopupOpen(false))
  }

  const dispatchUploadAttachments = (
    attachments: PostReceiptAttachmentRequest[],
  ): void => {
    uploadReceiptAttachments.mutate(attachments)
  }

  const deleteAttachment = (attachmentId: string): void => {
    dispatch(
      createSetReceiptAutoMatchAttachmentIds(
        receiptAttachments
          .filter((att) => att.id !== attachmentId)
          .map((att) => att.id),
      ),
    )
  }

  const dispatchAutoMatchReceipts = (): void => {
    const receiptIds = receiptAttachments.map((attachment) => attachment.id)
    dispatch(autoMatchReceipts(receiptIds))
  }

  return (
    <Popup
      onClickClose={closePopup}
      showCloseButton
      css={css`
        display: flex;
        position: relative;
      `}
    >
      <div
        css={css`
          width: 100%;
          display: flex;
          flex-direction: column;
          row-gap: 40px;
        `}
      >
        <div
          css={css`
            row-gap: 32px;
            display: flex;
            flex-direction: column;
          `}
        >
          <span
            css={css`
              ${getFontCss(
                32,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
            `}
          >
            {translate('Upload Receipts for Auto-Match')}
          </span>
          <span
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            {translate('You can upload up to 10 images at a time')}
          </span>
        </div>
        <AttachmentsList
          attachments={receiptAttachments}
          uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
          uploadAttachments={dispatchUploadAttachments}
          deleteAttachment={deleteAttachment}
          isReceiptRequired={false}
          setAttachmentError={setAttachmentError}
          attachmentLimit={10}
          quickDeleteIcon
          attachmentsText={
            <>
              <SvgReceiptUpload
                css={css`
                  transform: scale(1.6);
                  margin-bottom: 10px;
                `}
              />
              <span
                css={css`
                  ${getFontCss(
                    14,
                    themeConstants.colors.cta,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              >
                {translate('+ Add Receipt')}
              </span>
            </>
          }
        />
        <div
          css={css`
            display: flex;
            flex-direction: column;
          `}
        >
          {(attachmentError || receiptsAutoMatchError) && (
            <ErrorList
              errors={[attachmentError || receiptsAutoMatchError || <></>]}
              css={css`
                padding-left: 0;
              `}
            />
          )}
          <div
            css={css`
              display: flex;
              align-items: center;
              column-gap: 20px;
            `}
          >
            <Button
              onClick={dispatchAutoMatchReceipts}
              design={ButtonDesigns.designCta}
              type={ButtonTypes.button}
              big
              disabled={
                !uploadingAttachmentThumbnails ||
                !receiptAttachments ||
                Object.values(uploadingAttachmentThumbnails)?.length !== 0 ||
                receiptAttachments?.length === 0
              }
              text={
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                  `}
                >
                  {translate('Auto-Match Receipts')}
                </div>
              }
            />
            <HoverableLink onClick={closePopup} ariaLabel={'Cancel'}>
              {translate('Cancel')}
            </HoverableLink>
          </div>
        </div>
      </div>
    </Popup>
  )
}

export default UploadReceiptsAutoMatchPopup
